import { ChangeRideStatusForm } from "../types/forms/change-ride-status-form";
import {
  CourseEstimateRequest,
  CourseEstimateResult,
} from "../types/course-estimate";
import { DriverPriceForm } from "../types/forms/driver-price-form";
import { CourseForm, ContactCourseForm } from "../types/forms/course-form";
import { CourseParticipantsForm } from "../types/forms/course-participants-form";
import { Plateforme } from "../types/records/plateforme-record";
import { Course } from "../types/models/course";
import { CourseStatutDocuments } from "../types/models/course-statut-documents";
import { Fee } from "../types/models/fee";
import { Discount } from "../types/models/discount";
import { Refund } from "../types/models/refund";
import {
  ServiceCourse,
  ServiceCourseDetail,
} from "../types/models/service-course";
import { CreateCourseResult } from "../types/models/create-course-result";
import { UpdateCourseResult } from "../types/models/update-course-result";
import { HttpClient } from "./http-client";

export class CourseService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  async estimateCourse(
    request: CourseEstimateRequest,
  ): Promise<CourseEstimateResult> {
    return this.http.post<CourseEstimateResult>(
      "/api/courses/estimate",
      request,
    );
  }

  async getAllCourses(): Promise<Course[]> {
    return this.http.get<Course[]>("/api/courses/");
  }

  async getCoursesByContact(idContact: number): Promise<Course[]> {
    return this.http.get<Course[]>(`/api/courses/contact/${idContact}`);
  }

  async getAllCoursesByIdEntreprise(idEntreprise: number): Promise<Course[]> {
    return this.http.get<Course[]>(`/api/courses/entreprise/${idEntreprise}`);
  }

  async getCourseById(id: string | number): Promise<Course> {
    return this.http.get<Course>(`/api/courses/${id}`);
  }

  /**
   * Crée une course. La réponse n'est pas la course : le back renvoie l'id
   * créé et, pour un paiement en ligne, l'URL de la session Stripe.
   *
   * Le même endpoint sert aux réservations anonymes : l'ancien
   * `createCourseAnonyme` en était une copie exacte.
   */
  async createCourse(course: CourseForm): Promise<CreateCourseResult> {
    return this.http.post<CreateCourseResult>("/api/courses", course);
  }

  /**
   * Modifie une course. La réponse n'est pas la course : quand elle avait été
   * payée par Stripe, le back renvoie l'impact tarifaire de la modification
   * (complément à payer, remboursement, ou sans impact).
   */
  async updateCourse(
    courseId: number,
    course: Partial<CourseForm>,
    { admin = false }: { admin?: boolean } = {},
  ): Promise<UpdateCourseResult> {
    // Les horaires par arrêt ne sont portés que par la route admin (V2), dont le DTO exige
    // la plateforme appelante.
    return this.http.put<UpdateCourseResult>(
      `/api/${admin ? "v2/" : ""}courses/${courseId}`,
      admin ? { ...course, plateforme: Plateforme.Web } : course,
    );
  }

  async cancelCourse(courseId: number): Promise<void> {
    await this.http.post(`/api/courses/${courseId}/cancel`);
  }

  /**
   * L'endpoint attend un multipart/form-data (il accepte des pièces
   * jointes) : un corps JSON, comme cette méthode l'envoyait avant, est
   * rejeté par le back.
   */
  async changeStatusRide(
    courseId: number,
    data: ChangeRideStatusForm,
    documents?: File[],
  ): Promise<void> {
    const formData = new FormData();
    formData.append("Statut", String(data.statut));
    formData.append("DateChangement", new Date().toISOString());
    if (data.commentaire) formData.append("Commentaire", data.commentaire);
    (documents ?? []).forEach((file) => formData.append("Documents", file));

    await this.http.post(`/api/courses/${courseId}/statut`, formData);
  }

  async createPayment(courseId: number) {
    return this.http.post(`/api/courses/${courseId}/create-checkout-session`);
  }

  /**
   * Propose la course à un couple chauffeur/véhicule : le véhicule doit faire
   * partie de ceux que le chauffeur peut conduire.
   */
  async proposeAssignment(
    courseId: number,
    idChauffeur: number,
    idVehicule: number,
  ): Promise<void> {
    await this.http.post(`/api/courses/${courseId}/affectation/proposer`, {
      idChauffeur,
      idVehicule,
    });
  }

  /**
   * Ouvre la course au pool : tous les chauffeurs éligibles peuvent la prendre.
   * Une proposition en cours est retirée, véhicule compris.
   */
  async openToPool(courseId: number): Promise<void> {
    await this.http.post(`/api/courses/${courseId}/affectation/proposer`, {});
  }

  /** Désaffecte le chauffeur et son véhicule d'un seul coup. */
  async decommissioningDriverFromRide(courseId: number): Promise<void> {
    await this.http.patch(`/api/courses/${courseId}/desaffectation/chauffeur`);
  }

  async addRating(courseId: number, rating: any): Promise<void> {
    await this.http.patch(`/api/courses/${courseId}/avis`, rating);
  }

  async getFees(courseId: number): Promise<Fee[]> {
    return this.http.get<Fee[]>(`/api/courses/${courseId}/frais`);
  }

  async addFee(courseId: number, type: number, montant: number): Promise<Fee> {
    return this.http.post<Fee>(`/api/courses/${courseId}/frais`, {
      type,
      montant,
    });
  }

  async deleteFee(idCourse: number, idFrais: number): Promise<void> {
    await this.http.delete(`/api/courses/${idCourse}/frais/${idFrais}`);
  }

  async getDiscounts(courseId: number): Promise<Discount[]> {
    return this.http.get<Discount[]>(`/api/courses/${courseId}/ristournes`);
  }

  // Réservé au gestionnaire côté backend, comme la modification et le retrait.
  async addDiscount(
    courseId: number,
    type: number,
    montant: number,
  ): Promise<Discount> {
    return this.http.post<Discount>(`/api/courses/${courseId}/ristournes`, {
      type,
      montant,
    });
  }

  async updateDiscount(
    idCourse: number,
    idRistourne: number,
    type: number,
    montant: number,
  ): Promise<void> {
    await this.http.put(
      `/api/courses/${idCourse}/ristournes/${idRistourne}`,
      { type, montant },
    );
  }

  async deleteDiscount(idCourse: number, idRistourne: number): Promise<void> {
    await this.http.delete(
      `/api/courses/${idCourse}/ristournes/${idRistourne}`,
    );
  }

  async getRefunds(courseId: number): Promise<Refund[]> {
    return this.http.get<Refund[]>(`/api/courses/${courseId}/remboursements`);
  }

  /**
   * Pièces justificatives jointes au changement de statut de la course (ex.
   * photos prouvant l'absence du client), s'il y en a.
   */
  async getStatutDocuments(courseId: number): Promise<CourseStatutDocuments> {
    return this.http.get<CourseStatutDocuments>(
      `/api/courses/${courseId}/documents`,
    );
  }

  async loadStatutDocumentFile(
    courseId: number,
    documentId: number,
  ): Promise<Blob> {
    return this.http.getBlob(
      `/api/courses/${courseId}/documents/${documentId}`,
    );
  }

  async getAllServices(): Promise<ServiceCourse[]> {
    return this.http.get<ServiceCourse[]>("/api/courses/service");
  }

  async getServiceById(idService: number): Promise<ServiceCourseDetail> {
    return this.http.get<ServiceCourseDetail>(
      `/api/courses/service/${idService}`,
    );
  }

  async setDriverPrice(
    idCourse: number,
    dataNewPrice: DriverPriceForm,
  ): Promise<void> {
    await this.http.patch(`/api/courses/${idCourse}/prix-chauffeur`, {
      prixChauffeurInitial: dataNewPrice.driverInitialPrice,
      prixChauffeurFinal: dataNewPrice.driverFinalPrice,
    });
  }

  /**
   * Remplace d'un coup l'entreprise, le demandeur et le passager de la course, quel que soit son
   * statut (terminée ou annulée comprises). Remplace `defineFacturationCompanyForRide` et
   * `changePassagerForRide`, qui restent disponibles côté back.
   */
  async updateParticipants(
    courseId: number,
    participants: CourseParticipantsForm,
  ): Promise<void> {
    await this.http.put(
      `/api/v2/courses/${courseId}/participants`,
      participants,
    );
  }

  async defineFacturationCompanyForRide(
    courseId: number,
    idEntreprise: number,
  ): Promise<void> {
    await this.http.patch(`/api/courses/${courseId}/entreprise`, {
      idEntreprise,
    });
  }

  /**
   * Remplace le passager d'une course déjà réservée, avec un contact existant ou saisi à la
   * volée — jamais les deux. Réservé au back-office ou au demandeur de la course ; sans effet sur
   * le demandeur, qui ne change jamais.
   */
  async changePassagerForRide(
    courseId: number,
    passager: { idContactPassager: number | null; passager: ContactCourseForm | null },
  ): Promise<void> {
    await this.http.patch(`/api/courses/${courseId}/passager`, passager);
  }
}
